import { useEffect, useRef, useState } from 'react'
import { useApi } from '../lib/api'

interface PdfThumbnailProps {
  /** API path to fetch the PDF, e.g. /api/orders/42/pdf */
  src: string
  /** Width of the thumbnail in px (height is derived from A4 ratio) */
  width?: number
  /** Label shown in the modal title bar */
  label?: string
}

/**
 * Fetches a PDF from an authenticated API endpoint and renders it as:
 *   – a scaled-down visual thumbnail (click to open)
 *   – a full-screen modal with an embedded viewer on click
 */
export function PdfThumbnail({ src, width = 64, label }: PdfThumbnailProps) {
  const { fetchBlob } = useApi()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [open, setOpen]   = useState(false)
  const urlRef = useRef<string | null>(null)

  useEffect(() => {
    fetchBlob(src)
      .then((url) => { urlRef.current = url; setBlobUrl(url) })
      .catch(() => {/* PDF not yet generated or access denied */})
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current) }
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  const height = Math.round(width * 1.4142) // A4 aspect ratio

  if (!blobUrl) {
    return (
      <div
        style={{
          width, height,
          background: 'var(--color-bg, #f3f4f6)',
          border: '1px dashed var(--color-border, #d1d5db)',
          borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--color-muted, #9ca3af)',
          fontSize: 10,
        }}
        title="PDF wird generiert…"
      >
        PDF
      </div>
    )
  }

  // Scale-down factor: thumbnail width vs. A4 width (595.28 pt ≈ 595 px at 72 dpi)
  const scale = width / 595.28

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={label ? `${label} anzeigen` : 'PDF anzeigen'}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(true) }}
        title={label ?? 'PDF anzeigen'}
        style={{
          width, height,
          overflow: 'hidden',
          cursor: 'pointer',
          border: '1px solid var(--color-border, #d1d5db)',
          borderRadius: 4,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <iframe
          src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          title={label ?? 'PDF Vorschau'}
          style={{
            width: 595.28,
            height: 841.89,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            border: 'none',
            pointerEvents: 'none',
          }}
        />
      </div>

      {open && (
        <PdfModal src={blobUrl} label={label} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

interface PdfModalProps {
  src: string
  label?: string
  onClose: () => void
}

export function PdfModal({ src, label, onClose }: PdfModalProps) {
  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label ?? 'PDF Vorschau'}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.75)',
        zIndex: 400,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'white', borderRadius: 8,
        width: '100%', maxWidth: 860,
        height: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }}>
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border, #e5e7eb)',
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{label ?? 'Rechnung'}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <a
              href={src}
              download
              style={{ fontSize: 13, color: 'var(--color-primary, #2563eb)', textDecoration: 'none' }}
            >
              Herunterladen
            </a>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 20, lineHeight: 1, color: 'var(--color-muted, #6b7280)',
                padding: '0 4px',
              }}
              aria-label="Schließen"
            >
              ×
            </button>
          </div>
        </div>

        {/* PDF viewer */}
        <iframe
          src={`${src}#toolbar=1&navpanes=0`}
          title={label ?? 'PDF Vorschau'}
          style={{ flex: 1, border: 'none' }}
        />
      </div>
    </div>
  )
}
